/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  Switch,
  Tab,
  Tabs,
  Typography,
  useTheme
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import Loader from 'src/common/Loader';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import { setErrorInfo, setSuccessInfo } from 'src/redux/modalSlice';
import { getTankDetails, updateTank } from 'src/redux/tankSlice';
import { CONFIRMATION_MSG } from 'src/utils/constants';
import ROUTE from 'src/utils/routeConstants';
import useAuth from 'src/context/AuthContext';
import { getFormattedDate, hasAdminLikeAccess } from 'src/utils/getInitials';
import shouldDisplayDetailField from 'src/utils/detailFieldUtils';
import NewTank from './NewTank';
import TankFuelGraph from './TankFuelGraph';
import TankFuelEvents from './TankFuelEvents';
import TankTransactions from './TankTransactions';

const formatLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const isISODate = (value) => {
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
  return typeof value === 'string' && isoRegex.test(value);
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '--';
  if (typeof value === 'boolean') return value ? 'ON' : 'OFF';
  if (isISODate(value)) return getFormattedDate(value);
  return value;
};

const getDispenserName = (tank) => tank?.dispenser_name
  || tank?.dispenser?.station_name
  || tank?.dispenser?.name
  || tank?.dispenser?.dispenser_name
  || '--';

const shouldRenderField = (key, value) => {
  if (!shouldDisplayDetailField(key)) return false;
  if (typeof value === 'object' && value !== null) return false;

  const excludedFields = [
    'site',
    'current_balance',
    'dispenser_name',
    'dispenser',
    'unit_id',
    'unit_uuid',
    'fuel_graph_enabled',
    'fuel_graph_mode'
  ];
  return !excludedFields.includes(key);
};

const DetailBox = ({ label, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          bgcolor: alpha(theme.palette.primary.main, 0.02)
        }
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          mb: 0.5
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
};

function TankDetails() {
  const theme = useTheme();
  const { tankId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const hasAdminAccess = hasAdminLikeAccess(getUser);

  const { loading, tankDetails } = useSelector((state) => state.tanks);

  const [openModal, setOpenModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fuelGraphEnabled = Boolean(tankDetails?.fuel_graph_enabled);
  const requestedTab = new URLSearchParams(location.search).get('tab');
  const availableTabs = fuelGraphEnabled
    ? ['details', 'transactions', 'fuel', 'events']
    : ['details', 'transactions'];
  const activeTab = availableTabs.includes(requestedTab) ? requestedTab : 'details';
  const isTransactionsTab = activeTab === 'transactions';

  useEffect(() => {
    dispatch(getTankDetails(tankId));
  }, [dispatch, tankId]);

  const detailEntries = useMemo(() => (
    Object.entries(tankDetails || {}).filter(([key, value]) => shouldRenderField(key, value))
  ), [tankDetails]);

  const updateStatus = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);

    try {
      const values = {
        ...tankDetails,
        automation: !tankDetails.automation
      };

      await dispatch(updateTank(values)).unwrap();
      dispatch(setSuccessInfo({ successMsg: 'Tank status updated successfully!' }));
    } catch (error) {
      dispatch(
        setErrorInfo({
          errorMsg:
            error?.response?.data?.error_message
            || error?.message
            || 'Failed to update tank status'
        })
      );
    }

    setIsLoading(false);
  };

  const handleTabChange = (event, value) => {
    const tabQuery = value === 'details' ? '' : `?tab=${value}`;
    history.replace(`${ROUTE.tanks}/${tankId}${tabQuery}`);
  };

  if (loading || isLoading) return <Loader />;

  return (
    <Container
      maxWidth={isTransactionsTab ? false : 'lg'}
      sx={{
        mt: isTransactionsTab ? 0 : 4,
        mb: isTransactionsTab ? 0 : 4,
        px: isTransactionsTab ? { xs: 1, sm: 2 } : undefined,
        minHeight: isTransactionsTab ? 'calc(100vh - 64px)' : undefined,
        height: isTransactionsTab ? { xs: 'auto', md: 'calc(100vh - 64px)' } : undefined
      }}
    >
      <NewTank
        open={openModal}
        initialValues={tankDetails}
        handleClose={() => setOpenModal(false)}
      />

      <Card
        sx={{
          borderRadius: isTransactionsTab ? { xs: 2, md: 0 } : 3,
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.05)',
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: isTransactionsTab ? 'none' : 'calc(100vh - 140px)',
          height: isTransactionsTab ? { xs: 'auto', md: '100%' } : undefined,
        }}
      >
        <CardHeader
          sx={{
            p: isTransactionsTab ? { xs: 1.5, sm: 2 } : 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper'
          }}
          title={(
            <Box>
              <Typography variant="h6" fontWeight={700}>
                TANK DETAILS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage tank configuration
              </Typography>
            </Box>
          )}
          action={(
            <Button
              variant="outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => history.replace(ROUTE.tanks)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Back
            </Button>
          )}
        />

        <CardContent
          sx={{
            p: isTransactionsTab ? { xs: 1.5, sm: 2 } : 4,
            flex: 1,
            minHeight: 0,
            overflowY: activeTab === 'transactions' ? 'hidden' : 'auto',
            ...(activeTab === 'transactions' && {
              display: 'flex',
              flexDirection: 'column'
            })
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{ mb: 3, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}
          >
            <Tab value="details" label="Details" sx={{ textTransform: 'none', fontWeight: 700 }} />
            <Tab value="transactions" label="Transactions" sx={{ textTransform: 'none', fontWeight: 700 }} />
            {fuelGraphEnabled && (
              <Tab
                value="fuel"
                label={tankDetails?.fuel_graph_mode === 'track_fuel' ? 'Track + Fuel' : 'Fuel Graph'}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              />
            )}
            {fuelGraphEnabled && (
              <Tab value="events" label="Fuel Events" sx={{ textTransform: 'none', fontWeight: 700 }} />
            )}
          </Tabs>

          {activeTab === 'fuel' && <TankFuelGraph tank={tankDetails} />}
          {activeTab === 'events' && <TankFuelEvents tank={tankDetails} />}
          {activeTab === 'transactions' && (
            <TankTransactions tankId={tankId} siteId={tankDetails?.site} />
          )}
          {activeTab === 'details' && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4} key="dispenser_name">
                <DetailBox label="Dispenser Name">
                  <Typography variant="body1" fontWeight={600}>
                    {getDispenserName(tankDetails)}
                  </Typography>
                </DetailBox>
              </Grid>

              {detailEntries.map(([key, value]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  {key === 'automation' ? (
                    <DetailBox label="Automation">
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography fontWeight={600} color={value ? 'success.main' : 'error.main'}>
                          {value ? 'Enabled' : 'Disabled'}
                        </Typography>
                        <Switch
                          checked={value}
                          onChange={() => hasAdminAccess && setShowConfirmModal(true)}
                          disabled={!hasAdminAccess}
                          color="success"
                        />
                      </Box>
                    </DetailBox>
                  ) : (
                    <DetailBox label={formatLabel(key)}>
                      <Typography variant="body1" fontWeight={600}>
                        {formatValue(value)}
                      </Typography>
                    </DetailBox>
                  )}
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>

        {hasAdminAccess && (
          <>
            <Divider />
            <CardActions sx={{
              p: 2.5, justifyContent: 'flex-end', position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
            }}
            >
              <Button
                startIcon={<EditIcon />}
                variant="contained"
                onClick={() => setOpenModal(true)}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Edit Tank Details
              </Button>
            </CardActions>
          </>
        )}
      </Card>

      <ConfirmationDialog
        info={
          tankDetails?.automation
            ? CONFIRMATION_MSG.disableTank
            : CONFIRMATION_MSG.enableTank
        }
        open={showConfirmModal}
        disable={tankDetails?.automation}
        onClose={() => setShowConfirmModal(false)}
        onSubmit={updateStatus}
      />
    </Container>
  );
}

export default TankDetails;
